import { useState } from "react";
import { motion } from "motion/react";
import { Send, CheckCircle2, ShieldCheck } from "lucide-react";
import TurnstileWidget from "@/components/TurnstileWidget";

const GRAD = "#5048E5";
const fg = "hsl(var(--foreground))";
const fgMuted = "hsl(var(--muted-foreground))";
const border = "hsl(var(--border))";

const FONT = "Plus Jakarta Sans, sans-serif";
const HEAD = "Outfit, sans-serif";

const INPUT =
  "w-full rounded-xl px-4 py-3 text-sm outline-none transition-colors focus:ring-2";
const inputStyle: React.CSSProperties = {
  background: "hsl(var(--card))",
  border: `1px solid ${border}`,
  color: fg,
  fontFamily: FONT,
};

type SubmitResult = {
  success?: boolean;
  challenge?: boolean;
  siteKey?: string;
  error?: string;
};

/**
 * Public contact form. Posts to /api/contact, which handles spam protection
 * server-side. If the server responds asking for a human-verification
 * challenge, we render a Cloudflare Turnstile widget inline and resubmit with
 * the resulting token.
 */
export default function ContactForm() {
  const [name, setName] = useState("");
  const [email, setEmail] = useState("");
  const [company, setCompany] = useState("");
  const [message, setMessage] = useState("");
  const [website, setWebsite] = useState(""); // honeypot

  const [sending, setSending] = useState(false);
  const [sent, setSent] = useState(false);
  const [error, setError] = useState("");

  const [challengeSiteKey, setChallengeSiteKey] = useState<string | null>(null);

  async function submit(turnstileToken?: string) {
    setSending(true);
    setError("");
    try {
      const res = await fetch("/api/contact", {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({
          name: name.trim(),
          email: email.trim(),
          company: company.trim() || undefined,
          message: message.trim() || undefined,
          website, // honeypot — left blank by real users
          turnstileToken,
        }),
      });
      const data = (await res.json()) as SubmitResult;

      if (data.success) {
        setSent(true);
        setChallengeSiteKey(null);
        return;
      }

      // Server is asking us to complete a human-verification challenge.
      if (data.challenge && data.siteKey) {
        setChallengeSiteKey(data.siteKey);
        setError(data.error ?? "");
        return;
      }

      setError(data.error ?? "Something went wrong — please try again.");
    } catch {
      setError("Could not send your message — please try again.");
    } finally {
      setSending(false);
    }
  }

  function handleSubmit(e: React.FormEvent) {
    e.preventDefault();
    if (!name.trim()) {
      setError("Please enter your name.");
      return;
    }
    if (!/^\S+@\S+\.\S+$/.test(email.trim())) {
      setError("Please enter a valid email address.");
      return;
    }
    void submit();
  }

  if (sent) {
    return (
      <motion.div
        initial={{ opacity: 0, y: 12 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ duration: 0.4 }}
        className="flex flex-col items-center gap-3 rounded-2xl px-6 py-10 text-center"
        style={{ border: `1px solid ${border}`, background: "hsl(var(--card))" }}
      >
        <CheckCircle2 size={40} style={{ color: GRAD }} />
        <p className="text-lg font-bold" style={{ color: fg, fontFamily: HEAD }}>
          Thanks — your message is on its way
        </p>
        <p className="text-sm" style={{ color: fgMuted, fontFamily: FONT }}>
          We'll get back to you within 24 hours.
        </p>
      </motion.div>
    );
  }

  return (
    <form onSubmit={handleSubmit} className="flex flex-col gap-4">
      {/* Honeypot — visually hidden, ignored by real users, filled by bots. */}
      <div aria-hidden="true" className="absolute h-0 w-0 overflow-hidden">
        <label>
          Website
          <input
            type="text"
            tabIndex={-1}
            autoComplete="off"
            value={website}
            onChange={(e) => setWebsite(e.target.value)}
            name="website"
          />
        </label>
      </div>

      <div className="grid gap-4 sm:grid-cols-2">
        <input
          className={INPUT}
          style={inputStyle}
          placeholder="Your name *"
          value={name}
          onChange={(e) => setName(e.target.value)}
          maxLength={120}
          required
        />
        <input
          className={INPUT}
          style={inputStyle}
          placeholder="Email *"
          type="email"
          value={email}
          onChange={(e) => setEmail(e.target.value)}
          maxLength={255}
          required
        />
      </div>
      <input
        className={INPUT}
        style={inputStyle}
        placeholder="Company (optional)"
        value={company}
        onChange={(e) => setCompany(e.target.value)}
        maxLength={160}
      />
      <textarea
        className={INPUT}
        style={{ ...inputStyle, minHeight: 130, resize: "vertical" }}
        placeholder="How can we help?"
        value={message}
        onChange={(e) => setMessage(e.target.value)}
        maxLength={5000}
      />

      {challengeSiteKey && (
        <div className="flex flex-col gap-2 rounded-xl px-4 py-4" style={{ border: `1px solid ${border}`, background: "hsl(var(--card))" }}>
          <p className="flex items-center justify-center gap-2 text-center text-sm" style={{ color: fgMuted, fontFamily: FONT }}>
            <ShieldCheck size={15} style={{ color: GRAD }} />
            Just checking you're human — one tap and you're through.
          </p>
          <TurnstileWidget
            siteKey={challengeSiteKey}
            onVerify={(token) => void submit(token)}
            onError={() =>
              setError("Verification failed to load — please try again.")
            }
          />
        </div>
      )}

      {error && (
        <p className="text-sm" style={{ color: "hsl(14 100% 57%)", fontFamily: FONT }}>
          {error}
        </p>
      )}

      <button
        type="submit"
        disabled={sending}
        className="inline-flex items-center justify-center gap-2 rounded-xl px-6 py-3 text-sm font-bold text-white transition-opacity hover:opacity-90 disabled:opacity-60"
        style={{
          background: GRAD,
          fontFamily: FONT,
        }}
      >
        <Send size={16} />
        {sending ? "Sending…" : "Send message"}
      </button>
    </form>
  );
}
